import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  inject,
} from '@angular/core';
import { CommonModule, DecimalPipe } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import { startWith } from 'rxjs/operators';
import { NgbHighlight } from '@ng-bootstrap/ng-bootstrap';
import { Observable, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  Activity,
  ActivityParticipant,
} from '../../interfaces/activity.interfaces';
import { ActivityService } from '../../services/activity.service';
import { Student } from '../../interfaces/student.interfaces';
import { AuthService } from '../../services/auth.service';
import { Router } from '@angular/router';
import { VolunteerService } from '../../services/volunteer.service';
import { FolioPillComponent } from '../ui/folio-pill/folio-pill.component';
import { PaginationComponent } from '../ui/pagination/pagination.component';

@Component({
  selector: 'app-activity-list',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NgbHighlight,
    FolioPillComponent,
    PaginationComponent,
  ],
  templateUrl: './activity-list.component.html',
  styleUrls: ['./activity-list.component.scss'],
  providers: [DecimalPipe],
})
export class ActivityListComponent implements OnInit {
  private apiService = inject(ActivityService);
  private authService = inject(AuthService);
  private volunteerService = inject(VolunteerService);
  private cdr = inject(ChangeDetectorRef);
  private router = inject(Router);

  data: Activity[] = [];
  filteredData: Activity[] = [];
  page = 1;
  pageSize = 6;

  get pagedActivities(): Activity[] {
    const start = (this.page - 1) * this.pageSize;
    return this.filteredData.slice(start, start + this.pageSize);
  }
  registeredActivityIds = new Set<string>();
  activityToCancel: Activity | null = null;
  showCancelConfirmation = false;
  isCancelling = false;
  participants: ActivityParticipant[] = [];
  activityForParticipants: Activity | null = null;
  showParticipantsModal = false;
  isLoadingParticipants = false;
  activityStatusById = new Map<
  string,
  'registered' | 'attended' | 'absent' | 'validated' | 'cancelled'
>();
  // C3.1: folio de interacción (AC-...) por actividad para el usuario inscrito.
  folioByActivityId = new Map<string, string>();
  filter = new FormControl('', { nonNullable: true });
  isAdmin = false; // This should be directly set to true or false based on the user's ReactiveFormsModule
  userInfo$: Observable<Student>;
  currentRole: 'student' | 'admin' | 'volunteer' | null = null;
  volunteerAccountStatus: 'pending' | 'active' | 'inactive' | 'rejected' | null = null;
  studentAccountStatus: 'pending' | 'active' | 'inactive' | 'rejected' | null = null;
  isVolunteerStatusLoaded = false;
  activityToDelete: Activity | null = null;
  showDeleteConfirmation = false;
  isDeleting = false;

  constructor() {
    this.userInfo$ = this.authService.getUserInfo();
  }

  private readonly _destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.authService.user$
  .pipe(
    takeUntilDestroyed(this._destroyRef),
  )
  .subscribe((user) => {
    this.currentRole = user?.role ?? null;
    this.isAdmin = this.currentRole === 'admin';

    // FIX: admin y student NO dependen del estado de voluntario para ver la
    // lista. El gate `@else if (isVolunteerStatusLoaded)` del template se queda
    // en false si userInfo$ no emite (p.ej. admin -> GET /student/auth/:id 404),
    // dejando el cuerpo vacío. Lo liberamos aquí en cuanto conocemos el rol.
    if (this.currentRole !== 'volunteer') {
      this.isVolunteerStatusLoaded = true;
      this.cdr.detectChanges();
    }

    console.log('ACTIVITY LIST ROLE:', this.currentRole);
    console.log('🔎 USUARIO COMPLETO:', user);

    if (user?.role === 'volunteer' && user.id) {
      this.volunteerService
      .getMyProfile()
        .pipe(
          takeUntilDestroyed(this._destroyRef),
        )
        .subscribe({
          next: (volunteer) => {
            console.log(
              '🔎 VOLUNTARIO RECIBIDO:',
              volunteer,
            );

            console.log(
              '🔎 ACTIVIDADES DEL VOLUNTARIO:',
              volunteer.assignedActivities,
            );

            this.activityStatusById.clear();
            this.folioByActivityId.clear();

            (volunteer.assignedActivities ?? []).forEach(
              (activity) => {
                const activityId = String(
                  activity.activityRef ?? '',
                );

                if (!activityId) {
                  return;
                }

                const status =
                  activity.status ?? 'registered';

                this.activityStatusById.set(
                  activityId,
                  status,
                );

                if (activity.interactionFolio) {
                  this.folioByActivityId.set(
                    activityId,
                    activity.interactionFolio,
                  );
                }
              },
            );

            this.registeredActivityIds = new Set(
              (volunteer.assignedActivities ?? [])
                .filter(
                  (activity) =>
                    activity.status === 'registered',
                )
                .map((activity) =>
                  String(
                    activity.activityRef ?? '',
                  ),
                )
                .filter((id) => id !== ''),
            );

            console.log(
              '🔎 ESTADOS DEL VOLUNTARIO:',
              this.activityStatusById,
            );

            console.log(
              '🔎 ACTIVIDADES REGISTRADAS DEL VOLUNTARIO:',
              this.registeredActivityIds,
            );

            this.cdr.detectChanges();
          },

          error: (error) => {
            console.error(
              'ERROR OBTENIENDO VOLUNTARIO:',
              error,
            );
          },
        });
    }

    this.cdr.detectChanges();
  });
    this.apiService
      .getAllActivities()
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap((activities: Activity[]) => {
          console.log('ACTIVIDADES RECIBIDAS:', activities);
          console.log(
            'CUPOS:',
            activities.map((activity) => ({
              title: activity.title,
              maxParticipants: activity.maxParticipants,
              participantCount: activity.participantCount,
            })),
          );
        
          this.data = [...activities];
          this.filteredData = [...activities];
        
          console.log('ACTIVITY DATA:', this.data);
          console.log('ACTIVITY FILTERED DATA:', this.filteredData);
        
          this.cdr.detectChanges();
        }),
      )
      .subscribe();

      this.userInfo$
      .pipe(
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe((student) => {
        if (this.currentRole === 'volunteer') {
          this.volunteerService.getMyProfile().subscribe({
            next: (volunteer) => {
              this.volunteerAccountStatus = volunteer.accountStatus || null;
              this.isVolunteerStatusLoaded = true;
        
              this.cdr.detectChanges();
            },
            error: (error) => {
              console.error(
                '❌ ERROR OBTENIENDO ESTADO DEL VOLUNTARIO:',
                error,
              );
        
              this.isVolunteerStatusLoaded = true;
              this.cdr.detectChanges();
            },
          });
        } else {
          this.isVolunteerStatusLoaded = true;
        }
    if (!student) {
      return;
    }

    this.studentAccountStatus = student.accountStatus || null;

    console.log(
      '🔎 PERFIL RECIBIDO EN ACTIVITY LIST:',
      student
    );

    console.log(
      '🔎 ACCOUNT STATUS DEL STUDENT:',
      student.accountStatus
    );
    
    console.log(
      '🔎 ACTIVIDADES ASIGNADAS:',
      student.assignedActivities
    );
    this.activityStatusById.clear();
    this.folioByActivityId.clear();

    (student.assignedActivities ?? []).forEach(
      (activity) => {
        const activityId = String(
          activity.activityRef ?? '',
        );
    
        if (!activityId) {
          return;
        }

        if (activity.interactionFolio) {
          this.folioByActivityId.set(
            activityId,
            activity.interactionFolio,
          );
        }
    
        const currentStatus =
          this.activityStatusById.get(activityId);
    
        const newStatus =
          activity.status ?? 'registered';
    
        // Si ya existe un estado activo, no permitimos
        // que un registro "cancelled" lo reemplace.
        if (
          currentStatus &&
          currentStatus !== 'cancelled' &&
          newStatus === 'cancelled'
        ) {
          return;
        }
    
        this.activityStatusById.set(
          activityId,
          newStatus,
        );
      },
    );

this.registeredActivityIds = new Set(
  (student.assignedActivities ?? [])
    .filter(
      (activity) =>
        activity.status === 'registered',
    )
    .map((activity) =>
      String(activity.activityRef ?? ''),
    )
    .filter((id) => id !== ''),
);

    console.log(
      'ACTIVIDADES YA REGISTRADAS:',
      this.registeredActivityIds
    );

    this.cdr.detectChanges();
  });

    this.filter.valueChanges
    .pipe(
    startWith(''),
    takeUntilDestroyed(this._destroyRef),
    tap((term) => {
      this.filteredData = this.search(term);
      this.page = 1;
      this.cdr.detectChanges();
    }),
  )
  .subscribe();
  }

  refreshActivities(): void {
    this.apiService
      .getAllActivities()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (activities: Activity[]) => {
          console.log(
            'ACTIVIDADES ACTUALIZADAS:',
            activities,
          );
  
          console.log(
            'CUPOS ACTUALIZADOS:',
            activities.map((activity) => ({
              title: activity.title,
              maxParticipants:
                activity.maxParticipants,
              participantCount:
                activity.participantCount,
            })),
          );
  
          this.data = [...activities];
  
          this.filteredData = this.search(
            this.filter.value ?? '',
          );
  
          this.cdr.detectChanges();
        },
  
        error: (error: unknown) => {
          console.error(
            'ERROR ACTUALIZANDO ACTIVIDADES:',
            error,
          );
        },
      });
  }

  private search(text: string): Activity[] {
    const term = text
      .trim()
      .toLowerCase();
  
    if (!term) {
      return [...this.data];
    }
  
    return this.data.filter((activity) => {
      const title = String(activity.title ?? '').toLowerCase();
      const description = String(activity.description ?? '').toLowerCase();
      const type = String(activity.type ?? '').toLowerCase();
  
      const id = String(
        activity.idActividad ??
        activity.id ??
        ''
      ).toLowerCase();
  
      const hours = String(
        activity.valueInHours ?? ''
      ).toLowerCase();
  
      return (
        title.includes(term) ||
        description.includes(term) ||
        type.includes(term) ||
        id.includes(term) ||
        hours.includes(term)
      );
    });
  }

  trackById(index: number, activity: Activity): string {
    return activity.id;
  }

  openDeleteConfirmation(activity: Activity): void {
    this.activityToDelete = activity;
    this.showDeleteConfirmation = true;
  }
  
  closeDeleteConfirmation(): void {
    if (this.isDeleting) {
      return;
    }
  
    this.showDeleteConfirmation = false;
    this.activityToDelete = null;
  }
  
  confirmDeleteActivity(): void {
    if (!this.activityToDelete || this.isDeleting) {
      return;
    }
  
    const activityId = this.activityToDelete.id;
  
    this.isDeleting = true;
  
    this.apiService.deleteActivity(activityId).subscribe({
      next: () => {
        this.data = this.data.filter(
          (activity) => activity.id !== activityId
        );
  
        this.filteredData = this.filteredData.filter(
          (activity) => activity.id !== activityId
        );
  
        console.log(
          'ACTIVIDAD ELIMINADA:',
          activityId
        );
  
        this.isDeleting = false;
        this.showDeleteConfirmation = false;
        this.activityToDelete = null;
  
        this.cdr.detectChanges();
      },
  
      error: (error: unknown) => {
        console.error(
          'ERROR ELIMINANDO ACTIVIDAD:',
          error
        );
  
        this.isDeleting = false;
        this.cdr.detectChanges();
      },
    });
  }

  async registerActivity(activityId: string): Promise<void> {
    if (!this.canRegister(activityId)) {
      return;
    }
  
    try {
      console.log('REGISTRANDO ACTIVIDAD:', activityId);
  
      await this.apiService.registerActivity(activityId);
  
      this.registeredActivityIds.add(activityId);
      this.activityStatusById.set(activityId, 'registered');
  
      console.log('ACTIVIDAD REGISTRADA CORRECTAMENTE');
  
      this.refreshActivities();
    } catch (error) {
      console.error('ERROR AL REGISTRAR ACTIVIDAD:', error);
    }
  }

  openCancelConfirmation(activity: Activity): void {
    if (!this.canCancelRegistration(activity.id)) {
      return;
    }
  
    this.activityToCancel = activity;
    this.showCancelConfirmation = true;
  }
  
  closeCancelConfirmation(): void {
    if (this.isCancelling) {
      return;
    }
  
    this.showCancelConfirmation = false;
    this.activityToCancel = null;
  }
  
  confirmCancelRegistration(): void {
    if (!this.activityToCancel || this.isCancelling) {
      return;
    }
  
    const activityId = this.activityToCancel.id;
  
    this.isCancelling = true;
  
    this.apiService
      .cancelRegistration(activityId)
      .subscribe({
        next: () => {
          console.log(
            'REGISTRO CANCELADO:',
            activityId,
          );
        
          this.registeredActivityIds.delete(
            activityId,
          );
        
          this.activityStatusById.set(
            activityId,
            'cancelled',
          );
        
          this.isCancelling = false;
          this.showCancelConfirmation = false;
          this.activityToCancel = null;
        
          this.cdr.detectChanges();
          // Actualizar contador de participantes
          this.refreshActivities();
        },
  
        error: (error: unknown) => {
          console.error(
            'ERROR CANCELANDO REGISTRO:',
            error,
          );
  
          this.isCancelling = false;
          this.cdr.detectChanges();
        },
      });
  }

  isRegistered(activityId: string): boolean {
    return this.registeredActivityIds.has(activityId);
  }

  /** C3.1: folio de interacción (AC-...) del usuario para esta actividad, o null. */
  getActivityFolio(activityId: string): string | null {
    return this.folioByActivityId.get(activityId) ?? null;
  }

  viewParticipants(activityId: string): void {
    this.router.navigate(['/activity-list', activityId]);
  }

  openParticipantsModal(activity: Activity): void {
    this.activityForParticipants = activity;
    this.showParticipantsModal = true;
    this.isLoadingParticipants = true;
  
    this.apiService
      .getActivityParticipants(activity.id)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (students) => {
          console.log('🔎 PARTICIPANTES QUE RECIBE ANGULAR:', students);
          this.participants = students;
          this.isLoadingParticipants = false;
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('ERROR OBTENIENDO PARTICIPANTES:', error);
          this.participants = [];
          this.isLoadingParticipants = false;
          this.cdr.detectChanges();
        },
      });
  }

  closeParticipantsModal(): void {
    this.showParticipantsModal = false;
    this.activityForParticipants = null;
    this.participants = [];
  }

  getActivityStatus(
    activityId: string,
  ):
    | 'registered'
    | 'attended'
    | 'absent'
    | 'validated'
    | 'cancelled'
    | null {
  
    const status =
      this.activityStatusById.get(activityId) ?? null;
  
    console.log(
      'STATUS CONSULTADO:',
      activityId,
      status,
    );
  
    return status;
  }
  
  canRegister(activityId: string): boolean {
    if (
      this.currentRole === 'student' &&
      this.studentAccountStatus === 'inactive'
    ) {
      return false;
    }
  
    if (
      this.currentRole === 'volunteer' &&
      this.volunteerAccountStatus === 'inactive'
    ) {
      return false;
    }
  
    const status =
      this.getActivityStatus(activityId);
  
    return (
      status === null ||
      status === 'cancelled'
    );
  }
  
  canCancelRegistration(
    activityId: string,
  ): boolean {
    return (
      this.getActivityStatus(activityId) ===
      'registered'
    );
  }

  isActivityFull(activity: Activity): boolean {
    if (activity.maxParticipants === null) {
      return false;
    }
  
    return (
      activity.participantCount >=
      activity.maxParticipants
    );
  }
  
  getAvailableSpots(activity: Activity): number {
    if (activity.maxParticipants === null) {
      return Infinity;
    }
  
    return Math.max(
      activity.maxParticipants -
        activity.participantCount,
      0,
    );
  }
  
  getParticipantCountLabel(activity: Activity): string {
    if (activity.maxParticipants === null) {
      return activity.participantCount === 1
        ? '1 participante'
        : `${activity.participantCount} participantes`;
    }
  
    return `${activity.participantCount} / ${activity.maxParticipants} participantes`;
  }

}
